/**
 * Character Builder UI Panel (UI layer)
 *
 * DOM-based UI overlay for the character builder.
 * Engine code should not be imported here.
 */

export type BuilderTool = 'add' | 'remove' | 'paint'
export type BuilderSize = 'small' | 'medium' | 'large'

export interface BuilderUIState {
  selectedTool: BuilderTool
  selectedColorIndex: number
  selectedSize: BuilderSize
  symmetryEnabled: boolean
  animationPlaying: boolean
}

export interface BuilderUIActions {
  setTool: (tool: BuilderTool) => void
  setColor: (index: number) => void
  setSize: (size: BuilderSize) => void
  toggleSymmetry: () => void
  toggleZones: () => void
  toggleAnimation: () => void
  exit: () => void
}

const PANEL_ID = 'character-builder-panel'

const DEFAULT_PALETTE: [number, number, number][] = [
  [0.95, 0.8, 0.7],
  [0.85, 0.65, 0.5],
  [0.6, 0.4, 0.3],
  [1, 0, 0],
  [0, 0.8, 0],
  [0, 0.4, 1],
  [1, 1, 0],
  [1, 0.4, 0.7],
  [0.6, 0, 0.8],
  [1, 0.5, 0],
  [0, 0.8, 0.8],
  [1, 1, 1],
  [0.5, 0.5, 0.5],
  [0.1, 0.1, 0.1],
]

let panelElement: HTMLElement | null = null
let updateInterval: number | null = null

interface BuilderUIOptions {
  getState: () => BuilderUIState | null
  actions: BuilderUIActions
  palette?: [number, number, number][]
}

/**
 * Create and show the builder UI panel.
 * Returns cleanup function.
 */
export function createBuilderUI(options: BuilderUIOptions): () => void {
  const { getState, actions, palette = DEFAULT_PALETTE } = options
  const initialState = getState()

  if (!initialState) {
    console.warn('[BuilderUI] No state available; skipping panel creation')
    return () => {}
  }

  destroyBuilderUI()

  panelElement = document.createElement('div')
  panelElement.id = PANEL_ID
  panelElement.innerHTML = generatePanelHTML(initialState, palette)

  applyPanelStyles(panelElement)
  document.body.appendChild(panelElement)
  setupEventListeners(panelElement, actions)

  updateInterval = window.setInterval(() => {
    const currentState = getState()
    if (currentState && panelElement) {
      updatePanelState(panelElement, currentState)
    }
  }, 250)

  return () => {
    destroyBuilderUI()
  }
}

function destroyBuilderUI(): void {
  if (updateInterval !== null) {
    clearInterval(updateInterval)
    updateInterval = null
  }

  if (panelElement) {
    panelElement.remove()
    panelElement = null
  }

  const style = document.getElementById('builder-panel-styles')
  if (style) {
    style.remove()
  }
}

function generatePanelHTML(state: BuilderUIState, palette: [number, number, number][]): string {
  const colorSwatches = palette
    .map((color, index) => {
      const rgb = `rgb(${Math.round(color[0] * 255)}, ${Math.round(color[1] * 255)}, ${Math.round(color[2] * 255)})`
      const selected = index === state.selectedColorIndex ? 'selected' : ''
      return `<button class="color-swatch ${selected}" data-color-index="${index}" style="background-color: ${rgb}"></button>`
    })
    .join('')

  return `
    <div class="builder-header">
      <span class="builder-title">CHARACTER BUILDER</span>
      <button class="builder-close" id="builder-exit">&times;</button>
    </div>

    <div class="builder-section">
      <div class="section-label">TOOLS</div>
      <div class="tool-buttons">
        <button class="tool-btn ${state.selectedTool === 'add' ? 'active' : ''}" data-tool="add">+ Add</button>
        <button class="tool-btn ${state.selectedTool === 'remove' ? 'active' : ''}" data-tool="remove">- Remove</button>
        <button class="tool-btn ${state.selectedTool === 'paint' ? 'active' : ''}" data-tool="paint">&#127912; Paint</button>
      </div>
    </div>

    <div class="builder-section">
      <div class="section-label">COLOR</div>
      <div class="color-palette">${colorSwatches}</div>
    </div>

    <div class="builder-section">
      <div class="section-label">SIZE</div>
      <div class="size-buttons">
        <button class="size-btn ${state.selectedSize === 'small' ? 'active' : ''}" data-size="small">S</button>
        <button class="size-btn ${state.selectedSize === 'medium' ? 'active' : ''}" data-size="medium">M</button>
        <button class="size-btn ${state.selectedSize === 'large' ? 'active' : ''}" data-size="large">L</button>
      </div>
    </div>

    <div class="builder-section">
      <div class="section-label">OPTIONS</div>
      <div class="option-buttons">
        <button class="option-btn ${state.symmetryEnabled ? 'active' : ''}" id="toggle-symmetry">
          &harr; Symmetry
        </button>
        <button class="option-btn" id="toggle-zones">
          &#9723; Zones
        </button>
      </div>
    </div>

    <div class="builder-section">
      <div class="section-label">PREVIEW</div>
      <div class="preview-buttons">
        <button class="preview-btn ${state.animationPlaying ? 'active' : ''}" id="toggle-animation">
          ${state.animationPlaying ? '&#9208; Pause' : '&#9654; Play'}
        </button>
      </div>
    </div>

    <div class="builder-footer">
      <div class="shortcut-hints">
        <span>Q/W/R: Tools</span>
        <span>1/2/3: Size</span>
        <span>S: Symmetry</span>
        <span>G: Zones</span>
        <span>Space: Animate</span>
        <span>E: Exit</span>
      </div>
    </div>
  `
}

function applyPanelStyles(panel: HTMLElement): void {
  panel.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    width: 220px;
    background: rgba(20, 20, 25, 0.95);
    border: 2px solid rgba(100, 200, 255, 0.3);
    border-radius: 12px;
    padding: 12px;
    font-family: 'Segoe UI', system-ui, sans-serif;
    font-size: 12px;
    color: #fff;
    z-index: 10000;
    user-select: none;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  `

  const styleId = 'builder-panel-styles'
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style')
    style.id = styleId
    style.textContent = `
      #${PANEL_ID} .builder-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;
        padding-bottom: 8px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      }

      #${PANEL_ID} .builder-title {
        font-weight: bold;
        font-size: 14px;
        color: #64c8ff;
        letter-spacing: 1px;
      }

      #${PANEL_ID} .builder-close {
        background: none;
        border: none;
        color: #888;
        font-size: 18px;
        cursor: pointer;
        padding: 0 4px;
        transition: color 0.2s;
      }

      #${PANEL_ID} .builder-close:hover {
        color: #ff6464;
      }

      #${PANEL_ID} .builder-section {
        margin-bottom: 12px;
      }

      #${PANEL_ID} .section-label {
        font-size: 10px;
        color: #888;
        letter-spacing: 1px;
        margin-bottom: 6px;
      }

      #${PANEL_ID} .tool-buttons,
      #${PANEL_ID} .size-buttons,
      #${PANEL_ID} .option-buttons,
      #${PANEL_ID} .preview-buttons {
        display: flex;
        gap: 6px;
      }

      #${PANEL_ID} .tool-btn,
      #${PANEL_ID} .size-btn,
      #${PANEL_ID} .option-btn,
      #${PANEL_ID} .preview-btn {
        flex: 1;
        padding: 8px 4px;
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 6px;
        color: #ccc;
        cursor: pointer;
        font-size: 11px;
        transition: all 0.15s;
      }

      #${PANEL_ID} .tool-btn:hover,
      #${PANEL_ID} .size-btn:hover,
      #${PANEL_ID} .option-btn:hover,
      #${PANEL_ID} .preview-btn:hover {
        background: rgba(255, 255, 255, 0.1);
        border-color: rgba(255, 255, 255, 0.2);
      }

      #${PANEL_ID} .tool-btn.active,
      #${PANEL_ID} .size-btn.active,
      #${PANEL_ID} .option-btn.active,
      #${PANEL_ID} .preview-btn.active {
        background: rgba(100, 200, 255, 0.2);
        border-color: rgba(100, 200, 255, 0.5);
        color: #64c8ff;
      }

      #${PANEL_ID} .color-palette {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        gap: 4px;
      }

      #${PANEL_ID} .color-swatch {
        width: 100%;
        aspect-ratio: 1;
        border: 2px solid transparent;
        border-radius: 4px;
        cursor: pointer;
        transition: transform 0.1s, border-color 0.1s;
      }

      #${PANEL_ID} .color-swatch:hover {
        transform: scale(1.1);
        border-color: rgba(255, 255, 255, 0.5);
      }

      #${PANEL_ID} .color-swatch.selected {
        border-color: #64c8ff;
        box-shadow: 0 0 8px rgba(100, 200, 255, 0.5);
      }

      #${PANEL_ID} .builder-footer {
        margin-top: 12px;
        padding-top: 8px;
        border-top: 1px solid rgba(255, 255, 255, 0.1);
      }

      #${PANEL_ID} .shortcut-hints {
        display: flex;
        flex-wrap: wrap;
        gap: 4px 8px;
        font-size: 9px;
        color: #666;
      }

      #${PANEL_ID} .shortcut-hints span {
        background: rgba(255, 255, 255, 0.05);
        padding: 2px 4px;
        border-radius: 3px;
      }
    `
    document.head.appendChild(style)
  }
}

function setupEventListeners(panel: HTMLElement, actions: BuilderUIActions): void {
  // Event delegation: single listener handles all button clicks
  panel.addEventListener('click', (e) => {
    const target = e.target as HTMLElement

    // Tool buttons
    if (target.matches('.tool-btn')) {
      const tool = target.dataset.tool as BuilderTool
      if (tool) actions.setTool(tool)
      return
    }

    // Size buttons
    if (target.matches('.size-btn')) {
      const size = target.dataset.size as BuilderSize
      if (size) actions.setSize(size)
      return
    }

    // Color swatches
    if (target.matches('.color-swatch')) {
      const index = parseInt(target.dataset.colorIndex || '0', 10)
      actions.setColor(index)
      return
    }

    // Option buttons by ID
    switch (target.id) {
      case 'toggle-symmetry':
        actions.toggleSymmetry()
        break
      case 'toggle-zones':
        actions.toggleZones()
        break
      case 'toggle-animation':
        actions.toggleAnimation()
        break
      case 'builder-exit':
        actions.exit()
        break
    }
  })
}

function updatePanelState(panel: HTMLElement, state: BuilderUIState): void {
  panel.querySelectorAll('.tool-btn').forEach((btn) => {
    const tool = btn.getAttribute('data-tool')
    btn.classList.toggle('active', tool === state.selectedTool)
  })

  panel.querySelectorAll('.size-btn').forEach((btn) => {
    const size = btn.getAttribute('data-size')
    btn.classList.toggle('active', size === state.selectedSize)
  })

  panel.querySelectorAll('.color-swatch').forEach((btn) => {
    const index = parseInt(btn.getAttribute('data-color-index') || '0', 10)
    btn.classList.toggle('selected', index === state.selectedColorIndex)
  })

  const symmetryBtn = panel.querySelector('#toggle-symmetry')
  symmetryBtn?.classList.toggle('active', state.symmetryEnabled)

  const animBtn = panel.querySelector('#toggle-animation')
  if (animBtn) {
    animBtn.classList.toggle('active', state.animationPlaying)
    animBtn.textContent = state.animationPlaying ? '|| Pause' : '> Play'
  }
}
